// Tabula Communis: the Captain calls "Vocate" and the eight officers speak in
// turn. The order and each officer's subject are the module's §8 Beat 1
// (tabletop/modules/01-the-esperanca-window.md in canon); the words are what
// they said in session 01 (tabletop/sessions/session-01-the-esperanca-rescue.md).
// Constantin speaks last because he synthesizes, which is why this is not
// officers.ts's OFFICER_ORDER, where he sits first as Chief of Staff.
// This module only keeps time; src/tabulaPanel.ts draws the chairs and
// src/main.ts mirrors each line into the comms log.

import type { AppMode } from './appMode';
import { lineOpen, type LineGate, type RevealState } from './sensorReveal';

export interface TabulaLine {
  officerId: string;
  text: string;
  /** Holds `text` back until the bridge could know it; `dark` plays until then. */
  gate?: LineGate;
  dark?: string;
}

/** Seconds each officer holds the floor: eight of them is the §8 beat's ~40 s. */
export const SECONDS_PER_OFFICER = 5;

const BEAT_1: readonly TabulaLine[] = [
  { officerId: 'anastasia', text: 'Full burn, 19 minutes, or a slowed approach. Not both. I need the decision in 9 minutes.' },
  { officerId: 'bohdan', text: '17 souls on Esperança, six of them children. Casualty budget zero. Do not die.' },
  { officerId: 'adrienne', text: 'The reactor failure signature is wrong. That seal was breached from outside. This was not an accident.' },
  { officerId: 'yumiko', text: 'Olena is hailed. There is a second passive low-band signal in the volume. It has not declared. Whoever it is, is watching.' },
  { officerId: 'yves', text: 'Esperança is carrying the Bára Hloušková audit-brief against House Thorne-Vance Holdings, due at Tribunal in 19 sols. Lose the ship and the brief goes with it. That is the motive.' },
  { officerId: 'selene', text: 'Sleep, Pain and Trauma stocks are full or near. 16 of 17 recoverable. Yumiko, ask Olena which of the Schola observers is the oldest.' },
  {
    officerId: 'ioan',
    text: 'The passive signature is a Heritage Cutter, 22 light-minutes out in a hide position, picket-escort configured. Same pattern as Belka L1 in ’73. Recommend hail, maneuver, broadside ready, board on success.',
    gate: { npc: 'HMS Auriga', atLeast: 'typed' },
    dark: 'There is a passive signature in the volume. Profile not resolved yet. Recommend hail, maneuver, broadside ready, board on success.',
  },
  { officerId: 'constantin', text: 'Surface synthesized. 93-second shot-clock before the Engineer commits. Captain, the floor.' },
];

/** True for a keydown that calls the Tabula: V, not over the menu or the key list, and not a paste. */
export function tabulaKey(
  e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey'>,
  modeOn: (mode: AppMode) => boolean,
): boolean {
  return e.key.toLowerCase() === 'v' && !e.metaKey && !e.ctrlKey && !modeOn('menu') && !modeOn('controls');
}

/** What `line` says now: its text once its gate is open, else its dark variant. */
export function tabulaSays(
  line: TabulaLine,
  stateOf: (name: string) => RevealState,
  beatFired: (id: string) => boolean,
): string {
  return lineOpen(line.gate, stateOf, beatFired) ? line.text : (line.dark ?? line.text);
}

/** The Beat 1 round, in speaking order. */
export function tabulaLines(): readonly TabulaLine[] {
  return BEAT_1;
}

export interface TabulaHandle {
  isOpen(): boolean;
  /** Open or close the council. Returns whether it is now open. */
  toggle(): boolean;
  /**
   * Advance `dt` real seconds. Returns the indices into `tabulaLines()` that
   * take the floor now. Time only runs while open, so closing mid-round
   * pauses it, and a finished round never replays.
   */
  tick(dt: number): number[];
  /** How many officers have spoken this round. */
  spoken(): number;
  /** Closed, and the round back to its start: a restart or a load. */
  reset(): void;
}

export function createTabula(): TabulaHandle {
  let open = false;
  let elapsed = 0;
  let spoken = 0;

  return {
    isOpen: () => open,
    toggle() {
      open = !open;
      return open;
    },
    tick(dt) {
      if (!open) return [];
      const total = tabulaLines().length;
      if (spoken > 0) elapsed += dt;
      const due = Math.min(total, Math.floor(elapsed / SECONDS_PER_OFFICER + 1e-9) + 1);
      const now: number[] = [];
      while (spoken < due) now.push(spoken++);
      return now;
    },
    spoken: () => spoken,
    reset() {
      open = false;
      elapsed = 0;
      spoken = 0;
    },
  };
}
